import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { authClient } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { KeyRound, LinkIcon, Loader2, Mail, Shield } from "lucide-react";

const dashboardPath = "/dashboard/";
type SignInMode = "password" | "magic-link" | "otp";

export const Route = createFileRoute("/login")({
  component: LoginPage,
  beforeLoad: async () => {
    const { data } = await authClient.getSession();
    if (data) {
      throw redirect({ to: "/" });
    }
  },
});

function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [mode, setMode] = useState<SignInMode>("password");
  const [otpSent, setOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const setAuthMode = (nextMode: SignInMode) => {
    setMode(nextMode);
    setError("");
    setMessage("");
    setOtp("");
    setOtpSent(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setIsLoading(true);

    try {
      if (mode === "password") {
        const result = await authClient.signIn.email({
          email,
          password,
          callbackURL: dashboardPath,
        });

        if (result.error) {
          setError(result.error.message ?? "Invalid credentials");
        } else {
          window.location.href = dashboardPath;
        }
        return;
      }

      if (mode === "magic-link") {
        const result = await authClient.signIn.magicLink({
          email,
          callbackURL: dashboardPath,
          errorCallbackURL: "/dashboard/login",
        });

        if (result.error) {
          setError(result.error.message ?? "Could not send magic link");
        } else {
          setMessage("Check your email for a sign-in link.");
        }
        return;
      }

      if (!otpSent || !otp) {
        const result = await authClient.emailOtp.sendVerificationOtp({
          email,
          type: "sign-in",
        });

        if (result.error) {
          setError(result.error.message ?? "Could not send sign-in code");
        } else {
          setOtpSent(true);
          setMessage("Check your email for a sign-in code.");
        }
        return;
      }

      const result = await authClient.signIn.emailOtp({
        email,
        otp,
      });

      if (result.error) {
        setError(result.error.message ?? "Invalid sign-in code");
      } else {
        window.location.href = dashboardPath;
      }
    } catch {
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/50 px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary">
            <Shield className="size-6 text-primary-foreground" />
          </div>
          <CardTitle className="text-xl">r-auth Admin</CardTitle>
          <CardDescription>Sign in to manage your auth service</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4 grid grid-cols-3 rounded-md border bg-background p-1">
            <Button
              type="button"
              variant={mode === "password" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setAuthMode("password")}
              className="px-2"
            >
              <KeyRound className="size-4" />
              <span className="hidden sm:inline">Password</span>
            </Button>
            <Button
              type="button"
              variant={mode === "magic-link" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setAuthMode("magic-link")}
              className="px-2"
            >
              <LinkIcon className="size-4" />
              <span className="hidden sm:inline">Link</span>
            </Button>
            <Button
              type="button"
              variant={mode === "otp" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setAuthMode("otp")}
              className="px-2"
            >
              <Mail className="size-4" />
              <span className="hidden sm:inline">Code</span>
            </Button>
          </div>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            {mode === "password" ? (
              <div className="flex flex-col gap-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            ) : null}
            {mode === "otp" && otpSent ? (
              <div className="flex flex-col gap-2">
                <Label htmlFor="otp">Code</Label>
                <Input
                  id="otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  required
                />
              </div>
            ) : null}
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
            {message && (
              <p className="text-sm text-muted-foreground">{message}</p>
            )}
            <Button type="submit" disabled={isLoading} className="w-full">
              {isLoading ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : null}
              {mode === "password" ? "Sign in" : mode === "magic-link" ? "Send sign-in link" : otpSent && otp ? "Verify code" : "Send sign-in code"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
